import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Plus, Minus, Pencil, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const STATUS = {
  added: { label: "Aggiunto", cls: "border-[#10B981]/50 bg-[#10B981]/5", badge: "bg-[#10B981] text-white", Icon: Plus },
  removed: { label: "Rimosso", cls: "border-[#EF4444]/50 bg-[#EF4444]/5", badge: "bg-[#EF4444] text-white", Icon: Minus },
  modified: { label: "Modificato", cls: "border-[#F59E0B]/60 bg-[#F59E0B]/5", badge: "bg-[#F59E0B] text-white", Icon: Pencil },
};

const show = (f, v) => {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return "—";
  if (f === "options") return v.map((o) => `${o.label}${o.next_key ? ` → ${o.next_key}` : ""}${o.back_to_menu ? " (menu)" : ""}`).join("\n");
  if (f === "wait") return `${v.amount} ${v.unit}${v.next_key ? ` → ${v.next_key}` : ""}${v.template_name ? ` · template ${v.template_name}` : ""}`;
  if (f === "condition") return `${v.field} ${v.op} ${v.value ?? ""}\nSì → ${v.yes_key || "—"} · No → ${v.no_key || "—"}`;
  if (f === "action") return v.type && v.type !== "none" ? `${v.type}${v.ref ? `: ${v.ref}` : ""}` : "—";
  if (f === "updates") return v.map((u) => (u.kind === "tag" ? `tag ${u.value}` : `${u.field} = ${u.value}`)).join("\n");
  if (f === "capture") return v.field ? `campo ${v.field}${v.next_key ? ` → ${v.next_key}` : ""}` : "—";
  if (f === "triggers") return v.map((t) => `${t.type}: ${t.value || ""}`).join("\n");
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v).replace(/\n\u241e\n/g, "\n···\n");
};

function Change({ c, testid }) {
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-start text-xs" data-testid={testid}>
      <pre className="whitespace-pre-wrap font-sans rounded-md bg-[#EF4444]/10 text-[#991B1B] px-2 py-1.5 line-through decoration-[#EF4444]/40">{show(c.field, c.before)}</pre>
      <ArrowRight className="h-3.5 w-3.5 text-slate-400 mt-1.5" />
      <pre className="whitespace-pre-wrap font-sans rounded-md bg-[#10B981]/10 text-[#065F46] px-2 py-1.5">{show(c.field, c.after)}</pre>
    </div>
  );
}

export function VersionDiff({ automationId, versionId, against = "current", onBack, onRestore, busy }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    setD(null); setErr("");
    api.get(`/automations/${automationId}/versions/${versionId}/diff`, { params: { against } })
      .then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Confronto non riuscito"));
  }, [automationId, versionId, against]);
  if (err) return <p className="text-sm text-[#B91C1C] py-6 text-center" data-testid="version-diff-error">{err}</p>;
  if (!d) return <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;
  const s = d.summary;
  const changed = d.steps.filter((x) => x.status !== "unchanged");
  const noChanges = !changed.length && !d.meta.length;
  return (
    <div className="space-y-3" data-testid="version-diff">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="ghost" onClick={onBack} data-testid="version-diff-back" className="h-7 px-2 text-slate-600"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Cronologia</Button>
        <div className="flex items-center gap-1.5 text-xs text-slate-700 flex-wrap">
          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium" data-testid="version-diff-from">{d.from.label} <span className="text-slate-400 font-normal">· {fmt(d.from.saved_at)}</span></span>
          <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium" data-testid="version-diff-to">{d.to.label}{d.to.saved_at ? <span className="text-slate-400 font-normal"> · {fmt(d.to.saved_at)}</span> : null}</span>
        </div>
      </div>
      <div className="flex gap-2 text-[11px] flex-wrap" data-testid="version-diff-summary">
        <span className="rounded-full px-2 py-0.5 bg-[#10B981]/10 text-[#065F46] font-medium">+{s.added} aggiunti</span>
        <span className="rounded-full px-2 py-0.5 bg-[#EF4444]/10 text-[#991B1B] font-medium">−{s.removed} rimossi</span>
        <span className="rounded-full px-2 py-0.5 bg-[#F59E0B]/10 text-[#92400E] font-medium">~{s.modified} modificati</span>
        <span className="rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">{s.unchanged} invariati</span>
      </div>
      <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
        {noChanges && <p className="text-sm text-slate-500 py-6 text-center" data-testid="version-diff-none">Nessuna differenza: le due versioni sono identiche.</p>}
        {d.meta.length > 0 && (
          <div className="rounded-lg border border-slate-200 p-2.5 space-y-2" data-testid="version-diff-meta">
            <p className="text-xs font-semibold text-slate-700">Impostazioni generali</p>
            {d.meta.map((c) => (
              <div key={c.field} className="space-y-1">
                <p className="text-[11px] text-slate-500">{c.label}</p>
                <Change c={c} testid={`diff-meta-${c.field}`} />
              </div>
            ))}
          </div>
        )}
        {changed.map((st) => {
          const S = STATUS[st.status];
          const node = st.after || st.before || {};
          return (
            <div key={st.key} className={`rounded-lg border p-2.5 space-y-2 ${S.cls}`} data-testid={`diff-step-${st.key}`}>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${S.badge}`}><S.Icon className="h-3 w-3" /> {S.label}</span>
                <span className="text-xs font-medium text-slate-800 truncate">nodo {st.key}</span>
                <span className="text-[11px] text-slate-500 truncate flex-1">{st.title}</span>
              </div>
              {st.status !== "modified" && node.message && <pre className="whitespace-pre-wrap font-sans text-xs text-slate-700 bg-white/70 rounded-md px-2 py-1.5">{show("message", node.message)}</pre>}
              {st.status !== "modified" && node.options?.length > 0 && <p className="text-[11px] text-slate-600">Opzioni: {show("options", node.options).replace(/\n/g, " · ")}</p>}
              {st.changes.map((c) => (
                <div key={c.field} className="space-y-1">
                  <p className="text-[11px] text-slate-600 font-medium">{c.label}</p>
                  <Change c={c} testid={`diff-step-${st.key}-${c.field}`} />
                </div>
              ))}
            </div>
          );
        })}
        {s.unchanged > 0 && (
          <button type="button" onClick={() => setShowAll(!showAll)} data-testid="version-diff-toggle-unchanged" className="text-[11px] text-slate-500 hover:text-slate-800 underline">
            {showAll ? "Nascondi" : "Mostra"} i {s.unchanged} nodi invariati
          </button>
        )}
        {showAll && d.steps.filter((x) => x.status === "unchanged").map((st) => (
          <div key={st.key} className="rounded-lg border border-slate-200 px-2.5 py-1.5 flex items-center gap-2 text-xs text-slate-500" data-testid={`diff-step-${st.key}`}>
            <span className="font-medium text-slate-600">nodo {st.key}</span><span className="truncate">{st.title}</span>
          </div>
        ))}
      </div>
      {onRestore && (
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 pt-3">
          <p className="text-[11px] text-slate-500">{noChanges ? "Nessuna differenza: il ripristino non cambierebbe nulla." : `Il ripristino applica ${s.added} aggiunte, ${s.removed} rimozioni e ${s.modified} modifiche ai nodi${d.meta.length ? ` e ${d.meta.length} impostazioni` : ""}.`}</p>
          <Button size="sm" onClick={() => onRestore(d.from)} disabled={busy || noChanges} data-testid="version-diff-restore" className="h-8 text-white font-semibold shrink-0" style={{ backgroundColor: "#2F7D32" }}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />} Ripristina {d.from.label}
          </Button>
        </div>
      )}
    </div>
  );
}
